import {
  EMPTY_ARRAY,
  formattedUTCDay,
  getAgGridColId,
  useDefaultColumns,
  type CareOrder,
  type ColumnDef,
  type Leaves,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';

export const useCareOrderManagementColumns = () => {
  const { enableCareOrderExternalIDField, enableCareOrderSettleDateField, supportAdjustedSettlementDate } =
    useFeatureFlag();

  const columnDefinitions = useMemo(
    () =>
      new Map(
        compact([
          {
            type: 'id',
            field: 'OrderID',
            title: 'ID',
            initialWidth: 90,
          },
          {
            type: 'id',
            field: 'nettedOrderID',
            title: 'Netted Order ID',
            initialWidth: 120,
            enableRowGroup: true,
          },
          {
            type: 'date',
            field: 'SubmitTime',
            title: 'Submit Time',
            params: { milliseconds: true },
            initialWidth: 150,
            initialSort: '-',
            enableRowGroup: true,
          },
          {
            type: 'careOrderStatus',
            field: 'status',
            title: 'Status',
            initialWidth: 130,
            enableRowGroup: true,
          },
          {
            type: 'side',
            field: 'Side',
            title: 'Side',
            enableRowGroup: true,
          },
          {
            type: 'size',
            field: 'OrderQty',
            title: 'Order Qty',
            initialWidth: 100,
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'security',
            field: 'Symbol',
            title: 'Symbol',
            initialWidth: 160,
            enableRowGroup: true,
          },
          {
            type: 'size',
            field: 'WorkingQty',
            title: 'Working Qty',
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'counterparty',
            field: 'Counterparty',
            title: 'Counterparty',
            initialWidth: 180,
            enableRowGroup: true,
          },
          {
            type: 'text',
            field: 'Annotations.ETP.InitiatingFrom',
            title: 'Initiating From',
            initialWidth: 140,
            enableRowGroup: true,
          },
          { field: 'User', type: 'user', enableRowGroup: true },

          { field: 'TransactTime', type: 'date' },
          {
            type: 'size',
            field: 'AvailableQty',
            title: 'Remaining Qty',
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'size',
            field: 'CumQty',
            title: 'Filled Qty',
            params: {
              currencyField: 'Currency' satisfies Leaves<CareOrder>,
            },
          },
          {
            field: 'CumAmt',
            title: 'Cum Amt',
            type: 'size',
            initialHide: true,
            params: { currencyField: 'AmountCurrency' satisfies Leaves<CareOrder> },
          },
          {
            type: 'number',
            field: 'units',
            title: 'Units',
            initialHide: true,
            initialWidth: 100,
          },
          {
            type: 'text',
            field: 'fund',
            initialHide: true,
            enableRowGroup: true,
          },
          {
            type: 'text',
            field: 'Group',
            title: 'Group',
            initialWidth: 120,
            enableRowGroup: true,
          },
          enableCareOrderExternalIDField && {
            type: 'text',
            field: 'ExternalGroup',
            title: 'External ID',
            enableRowGroup: true,
          },
          enableCareOrderSettleDateField && {
            type: 'date',
            field: 'SettleDate',
            title: 'Settlement Date',
            enableRowGroup: true,
            params: { customFormatter: formattedUTCDay },
          },
          enableCareOrderSettleDateField &&
            supportAdjustedSettlementDate && {
              type: 'custom',
              field: 'Annotations.SettleOffset',
              title: 'Settle Offset',
              params: { valueFormatter: ({ value }) => (value ? `T+${value}` : '') },
              initialWidth: 100,
            },
          { field: 'Text', type: 'text' },
          {
            type: 'text',
            field: 'Annotations.FIX.OrderOriginationTrader',
            title: 'Origination Trader',
            initialHide: true,
          },
          {
            field: 'approvedMarkets',
            type: 'markets',
            title: 'Approved Markets',
            params: { emptyArrayText: 'All available' },
            initialHide: true,
            enableRowGroup: true,
          },
          {
            type: 'price',
            field: 'AvgPx',
            title: 'Filled Price (no fees)',
            initialHide: true,
            params: {
              assetField: 'Symbol' satisfies Leaves<CareOrder>,
            },
          },
          {
            type: 'text',
            field: 'Comments',
            title: 'Comments',
            initialHide: true,
          },
          {
            field: 'subAccounts',
            type: 'subAccount',
            title: 'Sub Account(s)',
          },
          {
            id: 'care-order-management-product-type',
            type: 'productType',
            initialHide: true,
            title: 'Product',
            initialWidth: 100,
          },
          {
            field: 'Timestamp',
            type: 'date',
            title: 'Updated at',
            params: { milliseconds: true },
          },
          { field: 'ClOrdID', type: 'id', initialHide: true, initialWidth: 80 },
          {
            field: 'LeavesQty',
            type: 'size',
            initialHide: true,
            params: { currencyField: 'Currency' satisfies Leaves<CareOrder> },
            title: 'Open Qty',
          },
          {
            field: 'Currency',
            type: 'currency',
            initialHide: true,
          },

          {
            field: 'AmountCurrency',
            type: 'currency',
            initialHide: true,
          },
        ] satisfies (ColumnDef<CareOrder> | false)[]).map(column => [getAgGridColId(column), column])
      ),
    [enableCareOrderExternalIDField, enableCareOrderSettleDateField, supportAdjustedSettlementDate]
  );

  return useDefaultColumns(EMPTY_ARRAY, columnDefinitions);
};
